import type { ReactNode } from 'react';

/**
 * One section of the public page, laid out as a ledger.
 *
 * The heading sits in a margin on the left, the way the hours sit in the
 * margin of the diary at the top of the page, and the section's content takes
 * the rest of the width. On a wide screen the heading stays in view while a
 * long section scrolls past it; on a phone it simply comes first.
 *
 * Every section is built on this, so the page has one left edge to read down
 * rather than a new layout at each heading.
 */
export function SiteSection({
  id,
  title,
  intro,
  children,
}: {
  readonly id?: string;
  readonly title: ReactNode;
  /** One or two paragraphs under the heading, in the margin. */
  readonly intro?: ReactNode;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <section id={id} className="border-t border-wall-line py-section">
      <div className="shell grid gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <h2 className="sign text-balance text-sign text-ink">{title}</h2>
            {intro !== undefined && (
              <div className="mt-5 max-w-[34rem] space-y-4 text-lead text-ink-muted">{intro}</div>
            )}
          </div>
        </div>
        <div className="min-w-0 lg:col-span-8">{children}</div>
      </div>
    </section>
  );
}
